import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material'
import { Save, Image as ImageIcon, Category } from '@mui/icons-material'
import AdminLayout from '../../components/AdminLayout'
import api from '../../api/axios'
import toast from 'react-hot-toast'

export default function SiteSettings() {
  const qc = useQueryClient()
  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroImageUrl: '',
    specialOfferTitle: '',
    specialOfferDescription: '',
    specialOfferCode: '',
    homepageCategories: [] as string[],
  })

  const { data, isLoading, error, refetch } = useQuery(
    ['admin:site-settings'],
    async () => {
      const res = await api.get('/admin/site-settings')
      return res.data
    },
    {
      retry: 1,
      onSuccess: (data) => {
        setFormData({
          heroTitle: data.heroTitle || '',
          heroSubtitle: data.heroSubtitle || '',
          heroImageUrl: data.heroImageUrl || '',
          specialOfferTitle: data.specialOfferTitle || '',
          specialOfferDescription: data.specialOfferDescription || '',
          specialOfferCode: data.specialOfferCode || '',
          homepageCategories: data.homepageCategories || [],
        })
      },
      onError: (err: any) => {
        console.error('Site settings query error:', err)
      }
    }
  )

  // Fetch all categories for selection (excluding adult-products category)
  const { data: categoriesData } = useQuery(
    ['admin:categories'],
    async () => {
      const res = await api.get('/admin/categories')
      // Filter out adult-products category from homepage category selection
      return (res.data.categories || []).filter((cat: any) => cat.slug !== 'adult-products')
    }
  )

  const mutation = useMutation(
    (data: typeof formData) => api.put('/admin/site-settings', data),
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['admin:site-settings'] })
        qc.invalidateQueries({ queryKey: ['site-settings'] })
        toast.success('Site settings updated successfully!')
      },
      onError: (err: any) => {
        const errorMsg = typeof err?.response?.data?.error === 'string'
          ? err.response.data.error
          : err?.message || 'Failed to update settings'
        toast.error(errorMsg)
        console.error('Update site settings error:', err)
      },
    }
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            Failed to load site settings
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {(() => {
              const err = (error as any)?.response?.data?.error
              if (typeof err === 'string') return err
              if (err?.message) return err.message
              return 'Please check your connection and try again'
            })()}
          </Typography>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
        </Paper>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Site Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your homepage hero section and special offers
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {/* Hero Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                Hero Section
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Hero Title"
                    value={formData.heroTitle}
                    onChange={(e) => setFormData({ ...formData, heroTitle: e.target.value })}
                    fullWidth
                    required
                    helperText="Main heading displayed in the hero section. Use a new line (press Enter) to split the title - the second line will appear in red."
                    placeholder="Discover Your\nNatural Beauty"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Hero Subtitle"
                    value={formData.heroSubtitle}
                    onChange={(e) => setFormData({ ...formData, heroSubtitle: e.target.value })}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    helperText="Subtitle/description text below the title"
                    placeholder="Premium beauty and skin therapy products..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Hero Image URL"
                    value={formData.heroImageUrl}
                    onChange={(e) => setFormData({ ...formData, heroImageUrl: e.target.value })}
                    fullWidth
                    required
                    type="url"
                    helperText="Full URL to the hero image"
                    placeholder="https://images.unsplash.com/photo-..."
                    InputProps={{
                      startAdornment: <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                  {formData.heroImageUrl && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Image Preview:
                      </Alert>
                      <Box
                        component="img"
                        src={formData.heroImageUrl}
                        alt="Hero preview"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                        sx={{
                          width: '100%',
                          maxHeight: 300,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Homepage Categories Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                Homepage Categories
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="homepage-categories-label">Select Categories to Display</InputLabel>
                    <Select
                      labelId="homepage-categories-label"
                      id="homepage-categories"
                      multiple
                      value={formData.homepageCategories}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                        setFormData({ ...formData, homepageCategories: value as string[] })
                      }}
                      input={<OutlinedInput label="Select Categories to Display" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((categoryId) => {
                            const category = categoriesData?.find((c: any) => c.id === categoryId)
                            return (
                              <Chip
                                key={categoryId}
                                label={category?.name || categoryId}
                                size="small"
                                icon={<Category />}
                              />
                            )
                          })}
                        </Box>
                      )}
                    >
                      {categoriesData?.map((category: any) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox checked={formData.homepageCategories.indexOf(category.id) > -1} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Select the categories you want to display on the homepage. Up to 6 categories will be shown. 
                    If no categories are selected, all categories will be displayed.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>

            {/* Special Offer Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                Special Offer Section
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Offer Title"
                    value={formData.specialOfferTitle}
                    onChange={(e) => setFormData({ ...formData, specialOfferTitle: e.target.value })}
                    fullWidth
                    required
                    helperText="Title of the special offer (e.g., 'Special Offer - 20% Off')"
                    placeholder="Special Offer - 20% Off"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Offer Description"
                    value={formData.specialOfferDescription}
                    onChange={(e) => setFormData({ ...formData, specialOfferDescription: e.target.value })}
                    fullWidth
                    required
                    multiline
                    rows={2}
                    helperText="Description text for the offer"
                    placeholder="Use code FIRST20 on your first order. Limited time only!"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Promo Code"
                    value={formData.specialOfferCode}
                    onChange={(e) => setFormData({ ...formData, specialOfferCode: e.target.value.toUpperCase() })}
                    fullWidth
                    required
                    helperText="Promo code for the offer (e.g., FIRST20)"
                    placeholder="FIRST20"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={mutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={mutation.isLoading}
                sx={{ minWidth: 150 }}
              >
                {mutation.isLoading ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </AdminLayout>
  )
}

